import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { getColorFromPercentage } from '../utils/colorUtils';
import LoadingSpinner from './LoadingSpinner';
import TechBadge from './TechIcon';

ChartJS.register(ArcElement, Tooltip, Legend);

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const ReportsList = ({ onLogout, userRole }) => {
  const { portfolio: portfolioParam, account: accountParam } = useParams();
  const navigate = useNavigate();

  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectConfigs, setProjectConfigs] = useState({});
  const [uploadingProject, setUploadingProject] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [technologyFilter, setTechnologyFilter] = useState('All');
  const fileInputRef = useRef(null);

  const currentView = accountParam ? 'projects' : portfolioParam ? 'accounts' : 'portfolios';

  const allTechnologies = useMemo(() => {
    const techSet = new Set();
    for (const p of hierarchy) {
      for (const a of p.accounts) {
        for (const proj of a.projects) {
          if (proj.projectType) techSet.add(proj.projectType);
        }
      }
    }
    return ['All', ...Array.from(techSet).sort()];
  }, [hierarchy]);

  const filteredHierarchy = useMemo(() => {
    if (technologyFilter === 'All') return hierarchy;
    return hierarchy
      .map(portfolio => ({
        ...portfolio,
        accounts: portfolio.accounts
          .map(account => ({
            ...account,
            projects: account.projects.filter(p => p.projectType === technologyFilter),
          }))
          .filter(account => account.projects.length > 0)
          .map(account => ({ ...account, projectCount: account.projects.length })),
      }))
      .filter(portfolio => portfolio.accounts.length > 0)
      .map(portfolio => ({
        ...portfolio,
        accountCount: portfolio.accounts.length,
        projectCount: portfolio.accounts.reduce((sum, a) => sum + a.projects.length, 0),
      }));
  }, [hierarchy, technologyFilter]);

  const selectedPortfolio = useMemo(() => {
    if (!portfolioParam || filteredHierarchy.length === 0) return null;
    return filteredHierarchy.find(p => p.portfolio === decodeURIComponent(portfolioParam)) || null;
  }, [portfolioParam, filteredHierarchy]);

  const selectedAccount = useMemo(() => {
    if (!accountParam || !selectedPortfolio) return null;
    return selectedPortfolio.accounts.find(a => a.account === decodeURIComponent(accountParam)) || null;
  }, [accountParam, selectedPortfolio]);

  const canUpload = userRole === 'admin' || userRole === 'moderator';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (onLogout) onLogout();
      return;
    }
    fetchHierarchy(token);
    if (canUpload) fetchProjectConfigs(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUpload, onLogout]);

  const fetchHierarchy = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/hierarchy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHierarchy(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        if (onLogout) onLogout();
        return;
      }
      setError('Failed to load reports. Please check if the server is running.');
      setLoading(false);
    }
  };

  const fetchProjectConfigs = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/projects-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const configMap = {};
      response.data.forEach(project => { configMap[project.name] = project; });
      setProjectConfigs(configMap);
    } catch (err) {
      console.error('Error fetching project configs:', err);
    }
  };

  const handleUploadClick = (e, projectName) => {
    e.stopPropagation();
    setUploadingProject(projectName);
    setUploadError(null);
    setUploadSuccess(null);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingProject) { setUploadingProject(null); return; }

    const token = localStorage.getItem('authToken');
    if (!token) { setUploadError('Not authenticated'); setUploadingProject(null); return; }

    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${API_URL}/projects-config/${encodeURIComponent(uploadingProject)}/upload-zip`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setUploadSuccess(`Successfully uploaded ZIP for ${uploadingProject}`);
      setTimeout(() => setUploadSuccess(null), 5000);
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Failed to upload file');
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploadingProject(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isZipProject = (projectName) => projectConfigs[projectName]?.sourceType?.toUpperCase() === 'ZIP';

  const handlePortfolioClick = (portfolio) => navigate(`/${encodeURIComponent(portfolio.portfolio)}`);
  const handleAccountClick = (account) => {
    if (selectedPortfolio) navigate(`/${encodeURIComponent(selectedPortfolio.portfolio)}/${encodeURIComponent(account.account)}`);
  };
  const handleProjectClick = (filename) => {
    if (selectedPortfolio && selectedAccount) {
      const projectName = filename.split('/')[0];
      navigate(`/${encodeURIComponent(selectedPortfolio.portfolio)}/${encodeURIComponent(selectedAccount.account)}/${encodeURIComponent(projectName)}`);
    }
  };

  const getScoreColor = (score) => {
    if (score === 'N/A' || score === null || score === undefined) return '#95a5a6';
    const numericScore = typeof score === 'number' ? score : parseFloat(score) || 0;
    return getColorFromPercentage((numericScore / 5.0) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return dateString; }
  };

  const calculateAverageScore = (reports) => {
    if (!reports || reports.length === 0) return null;
    const validScores = reports
      .map(r => r.overallScore)
      .filter(s => s !== 'N/A' && s !== null && s !== undefined)
      .map(s => typeof s === 'number' ? s : parseFloat(s) || 0);
    if (validScores.length === 0) return null;
    return validScores.reduce((a, b) => a + b, 0) / validScores.length;
  };

  const getAllReports = () => {
    const all = [];
    for (const p of filteredHierarchy) for (const a of p.accounts) all.push(...a.projects);
    return all;
  };

  if (loading) return <div className="flex items-center justify-center min-h-96"><LoadingSpinner /></div>;
  if (error) return (
    <div className="p-4">
      <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">{error}</div>
    </div>
  );

  const allReports = getAllReports();
  const averageScore = calculateAverageScore(allReports);
  const maxScore = 5.0;
  const isNA = averageScore === null;
  const numericScore = isNA ? 0 : averageScore;
  const percentage = numericScore ? (numericScore / maxScore) * 100 : 0;

  const chartData = {
    datasets: [{
      data: [numericScore, maxScore - numericScore],
      backgroundColor: [isNA ? '#95a5a6' : getColorFromPercentage(percentage), '#e9ecef'],
      borderWidth: 0,
    }]
  };
  const chartOptions = {
    rotation: -90, circumference: 180, cutout: '66%', maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } }
  };

  const getPortfolioAverageScore = (portfolio) => {
    const all = [];
    for (const a of portfolio.accounts) all.push(...a.projects);
    return calculateAverageScore(all);
  };
  const getAccountAverageScore = (account) => calculateAverageScore(account.projects);

  const getViewTitle = () => {
    if (currentView === 'portfolios') return 'Select a Portfolio';
    if (currentView === 'accounts') return selectedPortfolio ? `${selectedPortfolio.portfolio} - Accounts` : 'Accounts';
    if (currentView === 'projects') return selectedAccount ? `${selectedAccount.account} - Projects` : 'Projects';
    return '';
  };

  const ScoreCard = ({ score, label }) => {
    const color = getScoreColor(score);
    const pct = score !== null && score !== undefined && score !== 'N/A'
      ? ((typeof score === 'number' ? score : parseFloat(score) || 0) / 5.0 * 100).toFixed(1)
      : null;
    const displayScore = score === null || score === undefined || score === 'N/A'
      ? 'N/A'
      : (typeof score === 'number' ? score.toFixed(1) : parseFloat(score).toFixed(1));

    return (
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center text-white shadow-sm" style={{ backgroundColor: color }}>
          <span className="font-bold text-2xl leading-none">{displayScore}</span>
          {pct && <span className="text-sm font-semibold mt-1 text-white/90">{pct}%</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <input type="file" ref={fileInputRef} className="hidden" accept=".zip,application/zip" onChange={handleFileChange} />

      {uploadSuccess && (
        <div className="p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400">{uploadSuccess}</div>
      )}
      {uploadError && (
        <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">{uploadError}</div>
      )}

      {/* Overall Score Widget */}
      <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">Overall Average Score</h3>
        <div className="flex items-center justify-center">
          <div className="relative" style={{ width: '320px', height: '190px' }}>
            <Doughnut data={chartData} options={chartOptions} />
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-4">
              <span className="text-4xl font-bold" style={{ color: isNA ? '#95a5a6' : getColorFromPercentage(percentage) }}>
                {isNA ? 'N/A' : numericScore.toFixed(1)}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">out of {maxScore}</span>
              {!isNA && <span className="text-sm font-semibold mt-0.5" style={{ color: getColorFromPercentage(percentage) }}>{percentage.toFixed(1)}%</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Section Title & Technology Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{getViewTitle()}</h2>
        {allTechnologies.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Technology:</label>
            <div className="flex flex-wrap gap-1.5">
              {allTechnologies.map(tech => (
                <button
                  key={tech}
                  onClick={() => setTechnologyFilter(tech)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    technologyFilter === tech
                      ? 'bg-primary-600 text-white border-primary-600 dark:bg-primary-500 dark:border-primary-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  {tech}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Portfolio Cards */}
      {currentView === 'portfolios' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredHierarchy.map((portfolio, index) => {
            const avgScore = getPortfolioAverageScore(portfolio);
            return (
              <div key={index} onClick={() => handlePortfolioClick(portfolio)}
                className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{portfolio.portfolio}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{portfolio.accountCount} Account{portfolio.accountCount !== 1 ? 's' : ''} &middot; {portfolio.projectCount} Project{portfolio.projectCount !== 1 ? 's' : ''}</p>
                  </div>
                  <ScoreCard score={avgScore} label="Avg" />
                </div>
                <div className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-500">
                  View Accounts
                  <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Account Cards */}
      {currentView === 'accounts' && (selectedPortfolio ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {selectedPortfolio.accounts.map((account, index) => {
            const avgScore = getAccountAverageScore(account);
            return (
              <div key={index} onClick={() => handleAccountClick(account)}
                className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{account.account}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{account.projectCount} Project{account.projectCount !== 1 ? 's' : ''}</p>
                  </div>
                  <ScoreCard score={avgScore} label="Avg" />
                </div>
                <div className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-500">
                  View Projects
                  <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Portfolio "{decodeURIComponent(portfolioParam)}" not found</div>
      ))}

      {/* Project Cards */}
      {currentView === 'projects' && (selectedAccount ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {selectedAccount.projects.map((report, index) => {
            const showUploadButton = canUpload && isZipProject(report.projectName);
            const isUploading = uploadingProject === report.projectName;
            return (
              <div key={index} onClick={() => handleProjectClick(report.filename)}
                className="relative p-5 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-800">
                {showUploadButton && (
                  <button
                    onClick={(e) => handleUploadClick(e, report.projectName)}
                    disabled={isUploading}
                    className="absolute top-3 right-3 inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {isUploading ? (
                      <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    ) : (
                      <>
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" /></svg>
                        ZIP
                      </>
                    )}
                  </button>
                )}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{report.displayName || report.projectName}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formatDate(report.evaluationDate)}</p>
                    {report.projectType && (
                      <div className="mt-2">
                        <TechBadge projectType={report.projectType} />
                      </div>
                    )}
                  </div>
                  <ScoreCard score={report.overallScore} label="Score" />
                </div>
                <div className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-500">
                  View Report
                  <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Account "{decodeURIComponent(accountParam)}" not found</div>
      ))}

      {filteredHierarchy.length === 0 && (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          <p>{technologyFilter !== 'All' ? `No projects found for technology: ${technologyFilter}` : 'No reports available'}</p>
        </div>
      )}
    </div>
  );
};

export default ReportsList;
